import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import useCurrentUser from 'common/hooks/useCurrentUser';

import BusinessVerificationFlowSection from './BusinessVerificationFlowSection';
import { KybProcessContext } from './KybProcessContext';
import { SectionConfigurationContext } from './SectionConfigurationContext';
import { Section } from './types';
import { useKybNavigation } from './useKybNavigation';
import { fetchKybSubmissionData, fetchLatestKybProcess } from '../../../apis/legitimizer/kyb/api';
import { KybSubmissionData } from '../../../apis/legitimizer/types';
import { isDefined } from '../../../common/utils';
import { Content } from '../ComplianceContent';

type BusinessVerificationFlowRouteParams = {
  section: Section;
};

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-block-start: 50px;
`;

const emptySubmissionData: KybSubmissionData = {
  basicCompanyInfo: null,
  bankAccounts: null,
  beneficiaries: null,
};

const BusinessVerificationFlow = () => {
  const {
    params: { section },
  } = useRouteMatch<BusinessVerificationFlowRouteParams>();
  const { navigate } = useKybNavigation();
  const { processId, merchantId } = useContext(KybProcessContext);
  const sectionConfig = useContext(SectionConfigurationContext);
  const currentUser = useCurrentUser();
  const intl = useIntl();

  const { data: latestKybProcess = null, isFetching: isKybProcessRevalidating } = useQuery(
    ['fetch-latest-kyb-process', merchantId],
    () => fetchLatestKybProcess(merchantId),
  );

  const {
    data: submissionResponse = null,
    isLoading,
    refetch,
  } = useQuery(['/kyb/processes/submissions', processId], () => fetchKybSubmissionData(processId), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });

  if (!submissionResponse || isLoading) {
    return (
      <SpinnerContainer>
        <Spinner size={'large'} />
      </SpinnerContainer>
    );
  }

  if (!isKybProcessRevalidating && latestKybProcess?.state !== 'OPEN') {
    navigate('/information-submitted');
  }

  const isModifiedByDifferentUser =
    submissionResponse.lastUpdatedBy && currentUser.data?._id.$oid !== submissionResponse.lastUpdatedBy;

  const prefilledInfoForYouFeedback = isModifiedByDifferentUser
    ? intl.formatMessage({ id: 'compliance.kyb.infos.prefilled-info-for-you' })
    : undefined;

  const handleSubmitSuccess = async (section: string, skipStep?: boolean) => {
    const skip = skipStep ? 0 : 1;
    const nextSection =
      sectionConfig.enabledSections[sectionConfig.enabledSections.findIndex(s => s === section) + skip];
    await refetch();
    if (isDefined(nextSection)) {
      navigate(`/flow/${nextSection}`);
    } else {
      navigate('/flow/review');
    }
  };

  return (
    <Content>
      <BusinessVerificationFlowSection
        feedback={submissionResponse.review?.feedback ?? prefilledInfoForYouFeedback}
        section={section}
        submissionData={submissionResponse.data ?? emptySubmissionData}
        submissionDocuments={submissionResponse.documents}
        onSubmitSuccess={handleSubmitSuccess}
      />
    </Content>
  );
};

export default BusinessVerificationFlow;
